import { Injectable, inject } from '@angular/core';
import {ProjectData} from '../../common/model/project-data';
import {RenameProjectPopup} from '../popups/rename-project.popup';
import {DialogResult, GenericDialogPopup} from '../../common/components/generic-dialog.popup';
import {JoinProjectsPopup} from '../popups/join-projects.popup';
import {ComponentType} from '@angular/cdk/portal';
import {Router} from '@angular/router';
import {AuthService} from '../../common/services/auth.service';
import {Logger} from '../../common/utils/logger';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {Folder} from '../../common/model/folder';
import {Polygon} from '../models/polygon';
import {NotificationData} from "../../common/model/notification-data";
import {EnterpriseApiService} from "../../common/services/api/routes/enterprise-api.service";

@Injectable({
  providedIn: 'root'
})
export class PopupsService {
  dialog = inject(MatDialog);
  private auth = inject(AuthService);
  private router = inject(Router);
  private stringsService = inject(StringsService);
  private firebaseApi = inject(FirebaseApi);
  private enterpriseApi = inject(EnterpriseApiService);


  public openDialogRef<R>(componentType: ComponentType<any>, data?: any, fullWidth: boolean = false, disableClose: boolean = true): MatDialogRef<any, any> {
    const config: any = {
      disableClose: disableClose,
      data,
    };
    if (fullWidth) {
      config.panelClass = 'full-width-dialog';
    }
    return this.dialog.open(componentType, config);
  }

  public openDialog<R>(componentType: ComponentType<any>, data?: any, fullWidth: boolean = false, disableClose: boolean = false): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.openDialogRef(componentType, data, fullWidth, disableClose).afterClosed().subscribe((response) => {
        resolve(response);
      });
    });
  }

  public openPopup(title: string,
                   message: string,
                   positiveButtonText: string,
                   negativeButtonText: string,
                   isPositivePrompt: boolean = true,
                   neutralButtonText?: string,
                   htmlContent?: string, disableClose: boolean = false): Promise<DialogResult> {
    return this.openDialog<DialogResult>(GenericDialogPopup, {
      title,
      message,
      htmlContent,
      positiveButtonText,
      negativeButtonText,
      neutralButtonText,
      isPositivePrompt
    }, false, disableClose);
  }

  public openRenameProject(project: Folder, isNew: boolean): Promise<Folder> {
    const clone: Folder = Object.assign({}, project);
    const data = {
      project: clone,
      isNew
    };
    return this.openDialog(RenameProjectPopup, data);
  }

  public openJoinProjectsPopup(projects: ProjectData[]): Promise<string> {
    return this.openDialog(JoinProjectsPopup, projects);
  }

  openLoadFilesClearPopup(): Promise<DialogResult> {
    return this.openPopup(
      this.stringsService.getString('clear_map'),
      this.stringsService.getString('import_clear_map_text'),
      this.stringsService.getString('clear'),
      this.stringsService.getString('cancel'),
      false,
      this.stringsService.getString('keep_geometries')
    );
  }

  /**
   * A restore point could not be taken before a clear. Clearing anyway is
   * still undoable in this session, but not recoverable later -- the user
   * decides, and the safe answer is the default one.
   */
  confirmClearWithoutRestorePoint(): Promise<boolean> {
    return this.openPopup(
      this.stringsService.getString('restore_point_failed_title'),
      this.stringsService.getString('restore_point_failed_text'),
      this.stringsService.getString('clear_anyway'),
      this.stringsService.getString('cancel'),
      false
    ).then(result => result === DialogResult.POSITIVE);
  }

  openDeletePolygonConfirmation(polygon: Polygon, onConfirm) {
    // One string with the name in it: the word order is the language's
    // ("'Field 1' を削除"), not English verb-then-name.
    this.openPopup(polygon.name
        ? this.stringsService.getString('delete_named_title', undefined, polygon.name)
        : this.stringsService.getString('delete'),
      // A line was asked about as "this polygon".
      this.stringsService.getString(polygon.showSurface ? 'delete_polygon_text' : 'delete_line_text'),
      this.stringsService.getString('delete'),
      this.stringsService.getString('cancel'),
      false)
      .then((result: DialogResult) => {
        if (result === DialogResult.POSITIVE) {
          onConfirm();
        }
      });
  }

  /**
   * Contact support: the Support page's new request (dashboard/support/new),
   * where the request becomes a conversation with pictures, videos and files
   * (owner, 2026-11-02: one place for it, rather than a form and then email).
   * `message` starts it for a known topic (single sign-on changes, say).
   *
   * From the map editor it opens in a new tab, so the map stays as it was.
   * Resolves null: the page itself says when the request went, so callers
   * have nothing to confirm.
   */
  public openSupportPopup(message: string = ''): Promise<any> {
    if (!this.auth.currentUserInfo) {
      return Promise.resolve(null);
    }
    if (this.router.url.startsWith('/app/')) {
      window.open('/dashboard/support/new', '_blank', 'noopener');
    } else {
      this.router.navigate(['/dashboard/support/new'], {state: message ? {message} : {}});
    }
    return Promise.resolve(null);
  }

  /**
   * Resolves true when accepted, false when declined, and null when closed
   * without an answer -- it used to stay pending for ever, and anything
   * waiting on it with it.
   */
  showAccountLinkRequestPopup(notification: NotificationData): Promise<boolean | null> {
    const enterpriseUser = (notification.data && notification.data.from) || {};
    // The organisation's name is typed by its owner and lands in HTML.
    const esc = (v: string) => ('' + (v || '')).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const org = enterpriseUser.company && enterpriseUser.email
      ? `${esc(enterpriseUser.company)} (${esc(enterpriseUser.email)})`
      : esc(enterpriseUser.company || enterpriseUser.email || '');
    return new Promise((resolve, reject) => {
    this.openPopup(this.stringsService.getString('account_link_request'),
      this.stringsService.getString('account_link_request_prompt', this.stringsService.getLanguage(), org),
      this.stringsService.getString('accept'),
      this.stringsService.getString('reject'),
      true,
      this.stringsService.getString('close'),
      null,
      false).then(r => {
      Logger.log('Link Popup Result: ', r);
      if (r === DialogResult.POSITIVE) {
        this.enterpriseApi.acceptEnterpriseAccountLink(notification.id).then(() => {
          resolve(true);
        }).catch(err => {
          reject(err);
        })
      } else if (r === DialogResult.NEGATIVE) {
        this.enterpriseApi.rejectEnterpriseAccountLink(notification.id).then(() => {
          resolve(false);
        }).catch(err => {
          reject(err);
        })
      } else {
        resolve(null);
      }
    }, reject);
  });
  }
}
